<script setup lang="ts">
import type { Tenant } from '~/types/models'
import { Button } from '@/components/ui/button'
import { MoonStar, SunMedium } from 'lucide-vue-next'
import { applyWorkspaceBrandTheme, resolveClientFacingLogoUrl } from '~/lib/brandTheme'
import { isClientFacingAuthRoute, shouldFetchPublicWorkspaceContextForAuth } from '~/lib/brandingSurface'
import {
    PLATFORM_APP_ICON_PATH,
    PLATFORM_APP_NAME,
    PLATFORM_LOGO_DARK_PATH,
    PLATFORM_LOGO_LIGHT_PATH,
} from '~/lib/platformBrand'

const route = useRoute();
const { getHostname, isCustomDomain } = useDomainResolver();
const routeSource = computed(() => (typeof route.query.source === 'string' ? route.query.source : ''));
const isOnboardingRoute = computed(() => route.path === '/onboarding');
const publicWorkspaceBranding = useState<Partial<Tenant> | null>('public-workspace-branding', () => null);
const brandingState = ref<'idle' | 'loading' | 'resolved'>('idle');

const isPortalAuthRoute = computed(() => (
    route.path === '/auth/client-login'
    || route.path === '/accept-client-invite'
    || (route.path === '/auth/mfa' && route.query.source === 'client-login')
    || (route.path === '/auth/completing-sign-in' && route.query.source === 'client-login')
));
const supportsPublicBranding = computed(() => shouldFetchPublicWorkspaceContextForAuth(route.path));
const supportsClientFacingBranding = computed(() => isClientFacingAuthRoute(route.path, routeSource.value));
const isBrandedAuthExperience = computed(() => (
    supportsClientFacingBranding.value && Boolean(publicWorkspaceBranding.value)
));
const authBrandingContext = computed(() => {
    const tenantId = typeof route.query.tenantId === 'string' ? route.query.tenantId.trim() : '';
    const hostname = getHostname();
    const customDomain = isCustomDomain(hostname) ? hostname : '';

    return {
        tenantId,
        customDomain,
        expectsBranding: supportsPublicBranding.value && Boolean(tenantId || customDomain),
    };
});
const shouldHoldAuthBranding = computed(() => (
    supportsClientFacingBranding.value
    && authBrandingContext.value.expectsBranding
    && brandingState.value !== 'resolved'
));
const authAudience = computed<'default' | 'client' | 'branded'>(
    () => {
        if (shouldHoldAuthBranding.value) {
            return isPortalAuthRoute.value ? 'client' : 'branded';
        }
        if (isPortalAuthRoute.value) return 'client';
        if (isBrandedAuthExperience.value) return 'branded';
        return 'default';
    },
);
const workspaceLogoUrl = computed(() => (
    supportsClientFacingBranding.value
        ? resolveClientFacingLogoUrl(publicWorkspaceBranding.value)
        : ''
));
const workspaceName = computed(() => (
    supportsClientFacingBranding.value
        ? (
            publicWorkspaceBranding.value?.name
            || (shouldHoldAuthBranding.value ? 'Workspace' : PLATFORM_APP_NAME)
        )
        : PLATFORM_APP_NAME
));
const workspacePortalMessage = computed(() => (
    publicWorkspaceBranding.value?.paymentSettings?.portalWelcomeMessage
    || 'Access a calmer, branded client portal for projects, approvals, files, quotes, and billing.'
));
const authHeadline = computed(() => {
    if (authAudience.value === 'client' || authAudience.value === 'branded') {
        return `Sign in to ${workspaceName.value}.`;
    }

    return 'Run projects, client comms, approvals, and billing from one calm workspace.';
});
const authMessage = computed(() => {
    if (authAudience.value === 'client') {
        return workspacePortalMessage.value;
    }

    if (authAudience.value === 'branded') {
        return 'Secure access to your branded workspace, shared updates, and billing without dropping back to the default Vuey Studio look and feel.';
    }

    return 'Keep your team aligned, give clients a branded portal, and get paid without bouncing between tools.';
});
const authHighlights = computed(() => {
    if (authAudience.value === 'client') {
        return [
            {
                label: 'Projects',
                title: 'Shared delivery view',
            },
            {
                label: 'Approvals',
                title: 'Clear next steps',
            },
            {
                label: 'Billing',
                title: 'Quotes and invoices',
            },
        ];
    }

    if (authAudience.value === 'branded') {
        return [
            {
                label: 'Access',
                title: 'Secure branded sign-in',
            },
            {
                label: 'Updates',
                title: 'Shared workspace activity',
            },
            {
                label: 'Billing',
                title: 'One consistent experience',
            },
        ];
    }

    return [
        {
            label: 'Projects',
            title: 'Clear delivery flow',
        },
        {
            label: 'Portal',
            title: 'Client-ready updates',
        },
        {
            label: 'Billing',
            title: 'Quotes to invoices',
        },
    ];
});
const workspaceInitials = computed(() => (
    workspaceName.value
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || '')
        .join('')
        || 'VS'
));
const { isDark, toggleMode } = useThemeMode();

async function syncAuthBranding() {
    const { tenantId, customDomain, expectsBranding } = authBrandingContext.value;

    if (!expectsBranding) {
        brandingState.value = 'resolved';
        publicWorkspaceBranding.value = null;
        applyWorkspaceBrandTheme(null);
        return;
    }

    brandingState.value = 'loading';
    try {
        const response = await $fetch<{ tenant: Partial<Tenant> | null }>('/api/public/tenant-branding', {
            query: {
                tenantId: tenantId || undefined,
                hostname: customDomain || undefined,
            },
        });

        publicWorkspaceBranding.value = response?.tenant || null;
        applyWorkspaceBrandTheme(supportsClientFacingBranding.value ? (publicWorkspaceBranding.value || null) as Tenant | null : null);
    } catch (error) {
        console.warn('Unable to load public workspace branding:', error);
        publicWorkspaceBranding.value = null;
        applyWorkspaceBrandTheme(null);
    } finally {
        brandingState.value = 'resolved';
    }
}

await syncAuthBranding();

watch(
    () => [route.path, typeof route.query.tenantId === 'string' ? route.query.tenantId : '', getHostname()],
    () => {
        void syncAuthBranding();
    },
);
</script>

<template>
    <div class="relative min-h-screen overflow-hidden bg-muted/30">
        <div class="absolute right-5 top-5 z-20">
            <Button
                variant="outline"
                size="icon"
                class="rounded-full border-border/70 bg-card text-muted-foreground hover:bg-accent/70 hover:text-foreground"
                @click="toggleMode"
            >
                <SunMedium v-if="isDark" class="size-4" />
                <MoonStar v-else class="size-4" />
                <span class="sr-only">Toggle dark mode</span>
            </Button>
        </div>

        <div v-if="isOnboardingRoute" class="relative min-h-screen">
            <slot />
        </div>

        <div v-else class="relative flex min-h-screen">
            <div class="hidden lg:flex lg:w-[52%]">
                <div class="flex w-full flex-col justify-between border-r border-border/70 bg-card px-12 py-10 text-foreground">
                    <div class="space-y-8">
                        <img
                            v-if="authAudience === 'default'"
                            :src="isDark ? PLATFORM_LOGO_DARK_PATH : PLATFORM_LOGO_LIGHT_PATH"
                            alt="Vuey Studio"
                            class="h-10 w-auto object-contain"
                        >
                        <div class="inline-flex items-center gap-3 rounded-full border border-border/70 bg-muted/50 px-4 py-2 text-sm">
                            <div class="inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-muted text-sm font-semibold">
                                <img
                                    v-if="workspaceLogoUrl && !shouldHoldAuthBranding"
                                    :src="workspaceLogoUrl"
                                    :alt="`${workspaceName} logo`"
                                    class="h-full w-full object-cover"
                                >
                                <img
                                    v-else-if="authAudience === 'default'"
                                    :src="PLATFORM_APP_ICON_PATH"
                                    alt="Vuey Studio icon"
                                    class="h-full w-full object-cover"
                                >
                                <span v-else>{{ workspaceInitials }}</span>
                            </div>
                            <div>
                                <p class="font-semibold">{{ workspaceName }}</p>
                                <p class="text-xs text-muted-foreground">
                                    {{ authAudience === 'client' ? 'Client portal access' : authAudience === 'branded' ? 'Branded workspace access' : 'Client delivery workspace' }}
                                </p>
                            </div>
                        </div>

                        <div class="max-w-xl space-y-5">
                            <h1 class="text-4xl font-semibold leading-tight tracking-tight">
                                {{ authHeadline }}
                            </h1>
                            <p class="max-w-lg text-base leading-7 text-muted-foreground">
                                {{ authMessage }}
                            </p>
                        </div>
                    </div>

                    <div class="grid gap-4 sm:grid-cols-3">
                        <div
                            v-for="item in authHighlights"
                            :key="item.label"
                            class="border-l border-border/70 px-4 py-2 first:border-l-0"
                        >
                            <p class="text-xs uppercase tracking-[0.2em] text-muted-foreground">{{ item.label }}</p>
                            <p class="mt-2 text-sm font-semibold">{{ item.title }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex w-full items-center justify-center px-6 py-10 lg:w-[48%] lg:px-10">
                <div class="w-full max-w-lg rounded-xl border border-border/70 bg-card">
                    <slot />
                </div>
            </div>
        </div>
    </div>
</template>
